import { z } from "zod";

// Base type — used everywhere (charts, docs, messages, friends, header, etc.)
export const IdentifiedAuthor = z.object({
    type: z.literal("identified"),
    // Profile id — used by social surfaces to link the author chip to
    // /profile/$id. Always present (the row exists in User).
    id: z.number(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    // 1–2 char fallback used when avatarUrl is null and the client renders an
    // initials SVG. Always populated server-side via `computeInitials` so every
    // surface (web, navigator, emails) shows the same letters.
    initials: z.string().min(1).max(2),
    // Whether the user has social access turned on. Surfaces use this to
    // disable the profile link and italicize the name on inactive accounts.
    isSocialEnabled: z.boolean(),
    // Optional: some surfaces (documents table) label private-portal state too.
    isPrivateEnabled: z.boolean().optional(),
    // Server-computed identity check against the requesting user. Identity,
    // not permission — keep using userCanEdit for edit gating.
    isCurrentUser: z.boolean(),
    // PRTL-1348: the org this author acts under *in the context of the
    // surface being rendered* (e.g. the org a discussion participant was
    // added under) — not their global display org. Only populated on
    // org-scoped surfaces; absent everywhere else.
    organizationName: z.string().nullable().optional(),
});
export type IdentifiedAuthor = z.infer<typeof IdentifiedAuthor>;

// Anonymous author — seen by other users for anonymous posts/comments.
// `realName` is only populated for Compass superusers; omitted for everyone else.
export const AnonymousAuthor = z.object({
    type: z.literal("anonymous"),
    name: z.literal("Anonymous"),
    realName: z.string().optional(),
    isCurrentUser: z.boolean(),
});
export type AnonymousAuthor = z.infer<typeof AnonymousAuthor>;

// Anonymous self — seen by the posting user for their own anonymous posts/comments
export const AnonymousSelfAuthor = z.object({
    type: z.literal("anonymous-self"),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    isCurrentUser: z.boolean(),
});
export type AnonymousSelfAuthor = z.infer<typeof AnonymousSelfAuthor>;

// Compass Team author — used when a discussion has no individual author (authorId is null)
export const CompassTeamAuthor = z.object({
    type: z.literal("compass-team"),
    name: z.literal("Compass Team"),
    isCurrentUser: z.boolean(),
});
export type CompassTeamAuthor = z.infer<typeof CompassTeamAuthor>;

// Deleted author — used when the underlying User.deletedAt is set. Clients
// render the placeholder name without linking to the (gone) profile; see
// `buildIdentifiedAuthor` below for the branch used on the server side.
export const DELETED_USER_NAME = "Deleted User" as const;
export const DELETED_ACCOUNT_BANNER = "This account has been deleted." as const;

export const DeletedAuthor = z.object({
    type: z.literal("deleted"),
    name: z.literal(DELETED_USER_NAME),
    avatarUrl: z.null(),
    isCurrentUser: z.boolean(),
});
export type DeletedAuthor = z.infer<typeof DeletedAuthor>;

/**
 * Returns 1–2 uppercase letters for an avatar fallback.
 * - Both first + last present → first letter of each ("First Last" → "FL")
 * - Only one populated → its first letter
 * - Neither populated → "?" (caller should render the anonymous SVG instead;
 *   "?" is only used as a defensive fallback so the schema's min(1) holds)
 */
export function computeInitials(user: {
    firstName?: string | null;
    lastName?: string | null;
}): string {
    const first = (user.firstName ?? "").trim();
    const last = (user.lastName ?? "").trim();
    const f = first ? first.charAt(0).toUpperCase() : "";
    const l = last ? last.charAt(0).toUpperCase() : "";
    if (f && l) return f + l;
    if (f) return f;
    if (l) return l;
    return "?";
}

/**
 * Returns "First Last" or "First Last (Inactive)" if the user is
 * soft-deleted or has neither portal enabled.
 *
 * Used server-side when constructing any IdentifiedAuthor-shaped
 * payload so clients can render the name verbatim without each
 * screen having to reimplement the inactivity suffix.
 */
export function formatAuthorName(user: {
    firstName: string;
    lastName: string;
    isSocialEnabled?: boolean | null;
    isPrivateEnabled?: boolean | null;
    deletedAt?: Date | string | null;
}): string {
    if (user.deletedAt != null) return DELETED_USER_NAME;
    const baseName = `${user.firstName} ${user.lastName}`;
    const isDisabled = user.isSocialEnabled === false && user.isPrivateEnabled === false;
    return isDisabled ? `${baseName} (Inactive)` : baseName;
}

/**
 * Build the `DeletedAuthor | IdentifiedAuthor` payload for a user. Use this
 * at every server-side site that previously hand-rolled `{ type: "identified", ... }`
 * so a soft-deleted user consistently renders as "Deleted User" with no
 * avatar or profile link, while live-but-disabled users still fall through
 * to `formatAuthorName`'s "(Inactive)" suffix.
 */
export function buildIdentifiedAuthor(
    user: {
        id: number;
        firstName: string;
        lastName: string;
        isSocialEnabled: boolean;
        isPrivateEnabled?: boolean | null;
        deletedAt?: Date | string | null;
    },
    avatarUrl: string | null,
    requestingUserId: number,
    organizationName?: string | null,
): DeletedAuthor | IdentifiedAuthor {
    if (user.deletedAt != null) {
        // A deleted user can never be the authenticated requester.
        return { type: "deleted", name: DELETED_USER_NAME, avatarUrl: null, isCurrentUser: false };
    }
    return {
        type: "identified",
        id: user.id,
        name: formatAuthorName(user),
        avatarUrl,
        initials: computeInitials(user),
        isSocialEnabled: user.isSocialEnabled,
        isPrivateEnabled: user.isPrivateEnabled ?? undefined,
        isCurrentUser: user.id === requestingUserId,
        ...(organizationName !== undefined ? { organizationName } : {}),
    };
}

// Narrow union for surfaces (charts, documents, chart authors, thread author
// entry, etc.) that only ever produce an identified-or-deleted author — i.e.,
// no anonymous / compass-team variants apply.
export const IdentifiedOrDeletedAuthor = z.discriminatedUnion("type", [
    IdentifiedAuthor,
    DeletedAuthor,
]);
export type IdentifiedOrDeletedAuthor = z.infer<typeof IdentifiedOrDeletedAuthor>;

// Extended union — only for threads/comments/discussions/social posts
export const ThreadAuthor = z.discriminatedUnion("type", [
    IdentifiedAuthor,
    AnonymousAuthor,
    AnonymousSelfAuthor,
    CompassTeamAuthor,
    DeletedAuthor,
]);
export type ThreadAuthor = z.infer<typeof ThreadAuthor>;

/**
 * Canonical anonymity-aware author formatter for social surfaces (posts,
 * comments, discussion replies). Centralizes the four-way decision so call
 * sites don't reinvent the same `isAnonymous ? {...} : identified` ternary.
 *
 * - `isAnonymous=false` → `IdentifiedAuthor | DeletedAuthor` via `buildIdentifiedAuthor`
 * - `isAnonymous=true`, requester authored it → `AnonymousSelfAuthor` (their
 *    own real name suffixed with "(Anonymous)" so they recognize their own
 *    drafts in feeds)
 * - `isAnonymous=true`, requester is a Compass superuser viewing someone
 *    else's anonymous content → `AnonymousAuthor` with `realName` populated
 * - otherwise `AnonymousAuthor` with no `realName`
 */
export function buildSocialAuthor(
    user: {
        id: number;
        firstName: string;
        lastName: string;
        isSocialEnabled: boolean;
        isPrivateEnabled?: boolean | null;
        deletedAt?: Date | string | null;
    },
    opts: {
        isAnonymous: boolean;
        requestingUserId: number;
        isRequesterSuperuser: boolean;
        avatarUrl: string | null;
        // PRTL-1348: surface-scoped org label (see IdentifiedAuthor.organizationName).
        organizationName?: string | null;
    },
): ThreadAuthor {
    if (!opts.isAnonymous) {
        return buildIdentifiedAuthor(
            user,
            opts.avatarUrl,
            opts.requestingUserId,
            opts.organizationName,
        );
    }
    if (user.id === opts.requestingUserId) {
        return {
            type: "anonymous-self",
            name: `${user.firstName} ${user.lastName} (Anonymous)`,
            avatarUrl: opts.avatarUrl,
            isCurrentUser: true,
        };
    }
    return {
        type: "anonymous",
        name: "Anonymous",
        // Never true — the requester's own anonymous content is the
        // anonymous-self variant; true here would leak identity.
        isCurrentUser: false,
        ...(opts.isRequesterSuperuser ? { realName: `${user.firstName} ${user.lastName}` } : {}),
    };
}

/**
 * Flat-DTO counterpart of `DeletedAuthor` for payload shapes that cannot
 * become a discriminated union (User / PublicUser / SocialProfile / participant
 * rows that keep a real `id`). Identity-preserving when the user is live;
 * otherwise returns a copy with every identifying field replaced so a deleted
 * user's real name, email, avatar or bio never leaves the server, regardless
 * of which client field a screen happens to read.
 */
export function sanitizeDeletedUser<
    T extends {
        firstName: string;
        lastName: string;
        deletedAt?: Date | string | null;
        name?: string;
        initials?: string;
        email?: string | null;
        avatarUrl?: string | null;
        aboutMe?: string | null;
        aboutMeContentJson?: unknown;
        interests?: unknown;
        isSocialEnabled?: boolean;
    },
>(user: T): T {
    if (user.deletedAt == null) return user;
    const sanitized: T = {
        ...user,
        firstName: "Deleted",
        lastName: "User",
        isSocialEnabled: false,
    };
    if ("name" in user) sanitized.name = DELETED_USER_NAME;
    if ("initials" in user) sanitized.initials = "DU";
    if ("email" in user) sanitized.email = "";
    if ("avatarUrl" in user) sanitized.avatarUrl = null;
    if ("aboutMe" in user) sanitized.aboutMe = null;
    if ("aboutMeContentJson" in user) sanitized.aboutMeContentJson = null;
    if ("interests" in user) sanitized.interests = null;
    return sanitized;
}

/**
 * Display name for an organization row that may be soft-deleted. Deleted orgs
 * are excluded from pickers, but rows that already reference one (chart
 * grants, discussion participants, admin user lists) still need a label.
 */
export function formatOrganizationName(org: {
    name: string;
    deletedAt?: Date | string | null;
}): string {
    return org.deletedAt != null ? `${org.name} (deleted)` : org.name;
}
